import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ChevronDown, 
  ChevronRight,
  FileText,
  Users,
  Scale,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowDown,
  Info
} from 'lucide-react';

const flowchartSteps = [
  {
    id: 1,
    title: "Courrier préalable de régularisation",
    subtitle: "Délai : 1 à 3 mois",
    description: "Vous avez reçu un premier courrier de l'administration (souvent de la mairie) vous signalant une situation considérée comme irrégulière et vous demandant de vous mettre en conformité.",
    objective: "Montrer votre bonne foi et ouvrir un dialogue avec l'administration.",
    action: "Envoyer un courrier à la mairie dans lequel vous :\n– reconnaissez la réception du courrier\n– exprimez votre volonté de comprendre la situation\n– montrez votre intention de trouver une solution de régularisation",
    tips: [
      "Gardez une copie de tous vos échanges",
      "Envoyez votre courrier en recommandé avec accusé de réception",
      "Adoptez un ton courtois et constructif"
    ],
    infoBox: "Chaque réponse compte : elle pose les bases de la suite de la procédure.",
    status: "start",
    icon: FileText
  },
  {
    id: 2,
    title: "Relation avec la Mairie",
    subtitle: "Engagement des démarches",
    description: "Établissez un dialogue constructif avec votre mairie. Présentez votre projet et exprimez votre volonté d'intégration.",
    tips: [
      "La mairie a l'obligation de vous orienter vers les bonnes démarches",
      "Faites un compte rendu écrit de chaque échange",
      "Si pas de retour, le CR est valide 1 à 3 mois"
    ],
    infoBox: "À savoir : La mairie ou l'administration se doit de vous rediriger vers les bonnes démarches administratives. Obligation de loyauté de l'administration.",
    status: "process",
    icon: Users
  },
  {
    id: 3,
    title: "Visite DDT et/ou Mairie",
    subtitle: "Constatation pour dresser le PV d'infraction",
    description: "Les agents peuvent effectuer un contrôle. Ils peuvent constater depuis le domaine public ou sur le terrain.",
    tips: [
      "Montrez un lieu de vie entretenu",
      "Soyez courtois et présentez-vous sous votre meilleur jour",
      "Les constatations peuvent se faire sans besoin d'entrer"
    ],
    infoBox: "À savoir : Les constatations peuvent se faire depuis le bureau ou en terrain, sans besoin d'entrer sur votre propriété.",
    status: "warning",
    icon: AlertTriangle
  },
  {
    id: 4,
    title: "Réception du PV",
    subtitle: "Procès-verbal d'infraction",
    description: "Si un PV est dressé, vous le recevrez officiellement. C'est un moment clé où il faut réagir correctement.",
    tips: [
      "Vérifiez les vices de procédure",
      "Possibilité de demande de recours gracieux",
      "Vérifiez et commentez le dossier"
    ],
    actions: [
      "Prendre un avocat",
      "Demander l'aide juridictionnelle",
      "Utiliser l'aide juridique de son assurance"
    ],
    status: "critical",
    icon: FileText
  },
  {
    id: 5,
    title: "Convocation en gendarmerie",
    subtitle: "Audition",
    description: "Vous pouvez être convoqué pour une audition. Préparez-vous soigneusement, chaque élément évoqué pourra servir lors d'un éventuel procès.",
    tips: [
      "Vous pouvez utiliser votre droit au silence",
      "Établissez la stratégie avec votre avocat",
      "Préparez votre audition en amont"
    ],
    infoBox: "Rappel : Vous pouvez utiliser votre droit au silence. Établissez la stratégie avec votre avocat avant l'audition.",
    outcomes: [
      "Le procureur peut renvoyer en justice",
      "Sanction en justice",
      "Classer le dossier / l'affaire"
    ],
    status: "critical",
    icon: Scale
  },
  {
    id: 6,
    title: "Convocation au procès",
    subtitle: "Préparation du dossier",
    description: "Si le procureur décide de poursuivre, vous recevrez une convocation au procès. C'est le moment de préparer votre défense.",
    tips: [
      "Trouvez les décisions de justice existantes favorables",
      "Apportez les preuves de votre bonne foi",
      "Décrivez votre projet de façon cohérente"
    ],
    actions: [
      "Insister sur l'aspect sécurité (accès pompier, traitement des eaux usées...)",
      "Étude d'impact (écosystèmes, habitats)",
      "Rassembler tous les documents prouvant vos démarches"
    ],
    status: "critical",
    icon: Scale
  },
  {
    id: 7,
    title: "Procès",
    subtitle: "Audience au tribunal",
    description: "Le jour du procès, présentez votre défense avec votre avocat. Restez calme et factuel.",
    tips: [
      "Arrivez en avance",
      "Habillez-vous correctement",
      "Laissez votre avocat mener les échanges"
    ],
    status: "final",
    icon: Scale
  },
  {
    id: 8,
    title: "Si astreinte",
    subtitle: "Après le procès",
    description: "En cas de jugement défavorable avec astreinte (amende journalière), plusieurs options s'offrent à vous.",
    tips: [
      "Voir si une mise en faillite est possible",
      "Demande de recours gracieux",
      "Renvoi au tribunal administratif pour abus ou détournement de pouvoir"
    ],
    status: "end",
    icon: AlertTriangle
  }
];

const statusColors = {
  start: 'bg-secondary text-secondary-foreground',
  process: 'bg-accent text-accent-foreground',
  warning: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-primary/10 text-primary border-primary/30',
  final: 'bg-primary text-primary-foreground',
  end: 'bg-muted text-muted-foreground'
};

export default function Flowchart() {
  const [expandedSteps, setExpandedSteps] = useState([1]);

  const toggleStep = (id) => {
    setExpandedSteps(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  const expandAll = () => {
    setExpandedSteps(flowchartSteps.map(s => s.id));
  };

  const collapseAll = () => {
    setExpandedSteps([]);
  };

  return (
    <div className="min-h-screen" data-testid="flowchart-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Parcours juridique
          </h1>
          <p className="text-muted-foreground">
            Comprenez où vous en êtes et visualisez les prochaines étapes de votre parcours.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="flex justify-end gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-btn">
            Tout déplier
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">
            Tout replier
          </Button>
        </div>

        {/* Info card */}
        <Card className="mb-8 border-secondary/30 bg-secondary/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-secondary mt-0.5" />
              <div>
                <p className="font-medium text-secondary mb-1">Comment utiliser ce parcours ?</p>
                <p className="text-sm text-muted-foreground">
                  Ce logigramme s'adresse exclusivement aux personnes rencontrant des difficultés administratives liées à leur installation en habitat léger.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Il vous permet de comprendre où vous en êtes dans vos démarches et d'identifier les prochaines étapes à suivre. Cliquez sur chaque étape pour accéder aux informations détaillées, aux conseils et aux actions à entreprendre.
                </p>
                <p className="text-sm text-muted-foreground mt-2 font-medium">
                  Chaque situation étant unique, ces éléments doivent être adaptés à votre cas.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Flowchart */}
        <div className="space-y-4">
          {flowchartSteps.map((step, index) => {
            const Icon = step.icon;
            const isExpanded = expandedSteps.includes(step.id);
            const isLast = index === flowchartSteps.length - 1;

            return (
              <div key={step.id}>
                <Card 
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md ${isExpanded ? 'ring-1 ring-primary/20' : ''}`}
                  onClick={() => toggleStep(step.id)}
                  data-testid={`step-card-${step.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-sm flex items-center justify-center shrink-0 ${statusColors[step.status]}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              Étape {step.id}
                            </Badge>
                            {step.subtitle && (
                              <span className="text-xs text-muted-foreground">
                                {step.subtitle}
                              </span>
                            )}
                          </div>
                          <CardTitle className="text-lg">{step.title}</CardTitle>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="shrink-0">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardContent className="pt-0">
                      <div className="pl-16 space-y-4">
                        <p className="text-muted-foreground">{step.description}</p>

                        {step.objective && (
                          <div className="bg-primary/5 rounded-sm p-3 text-sm">
                            <p><span className="font-semibold text-primary">Objectif :</span> {step.objective}</p>
                          </div>
                        )}

                        {step.action && (
                          <div>
                            <h4 className="font-medium text-sm mb-2">Action à entreprendre :</h4>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">{step.action}</p>
                          </div>
                        )}

                        {step.infoBox && (
                          <div className="bg-accent/50 rounded-sm p-3 text-sm">
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                              <p>{step.infoBox}</p>
                            </div>
                          </div>
                        )}

                        {step.tips && step.tips.length > 0 && (
                          <div>
                            <h4 className="font-medium text-sm mb-2">Conseils :</h4>
                            <ul className="space-y-1">
                              {step.tips.map((tip, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <CheckCircle className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {step.actions && step.actions.length > 0 && (
                          <div>
                            <h4 className="font-medium text-sm mb-2">Actions possibles :</h4>
                            <div className="flex flex-wrap gap-2">
                              {step.actions.map((action, i) => (
                                <Badge key={i} variant="secondary" className="font-normal">
                                  {action}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {step.outcomes && step.outcomes.length > 0 && (
                          <div>
                            <h4 className="font-medium text-sm mb-2">Issues possibles :</h4>
                            <ul className="space-y-1">
                              {step.outcomes.map((outcome, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <ArrowDown className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                  {outcome}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Connector */}
                {!isLast && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-8 bg-border" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Card className="mt-8">
          <CardContent className="py-6 text-center">
            <h3 className="font-semibold mb-2">Besoin d'aide pour comprendre où vous en êtes ?</h3>
            <p className="text-muted-foreground mb-4">
              Notre assistant IA peut vous aider à identifier votre situation et vous orienter.
            </p>
            <Button asChild>
              <a href="/assistant">Parler à l'assistant</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
